import React from 'react';

const defaultOrder = ['personalInfo', 'summary', 'education', 'skills', 'professionalExperience', 'projects', 'certifications', 'awards'];

const ProTemplate = ({ data, layout, sectionVisibility, sectionOrder, theme }) => {
  const order = sectionOrder || defaultOrder;
  const visibleSections = order.filter(s => sectionVisibility[s] !== false);
  const isTwoColumn = layout === 'two-column';

  const fmtDate = (d) => d || '';

  const renderContact = (p) => {
    const items = [];
    if (p.email) items.push(p.email);
    if (p.phone) items.push(p.phone);
    if (p.address) items.push(p.address);
    return items;
  };

  const SectionTitle = ({ title }) => (
    <div style={{ borderBottom: `2px solid ${theme.accentColor || '#3498db'}`, marginBottom: 12, paddingBottom: 6 }}>
      <span style={{ fontSize: 16, fontWeight: 700, color: theme.primaryColor || '#2c3e50', textTransform: 'uppercase', letterSpacing: 1 }}>
        {title}
      </span>
    </div>
  );

  const renderSkills = () => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {data.skills.map((s, i) => (
        <span key={i} style={{
          background: `${theme.accentColor}15`,
          color: theme.accentColor || '#3498db',
          padding: '4px 12px',
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 500
        }}>{s}</span>
      ))}
    </div>
  );

  const renderExperienceItem = (exp) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: theme.primaryColor || '#2c3e50' }}>{exp.position}</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>{exp.company}{exp.location ? `, ${exp.location}` : ''}</div>
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {fmtDate(exp.startDate)} - {fmtDate(exp.endDate) || 'Present'}
        </div>
      </div>
      {exp.description && <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginTop: 6 }}>{exp.description}</div>}
    </div>
  );

  const renderEducationItem = (edu) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: theme.primaryColor || '#2c3e50' }}>{edu.school}</div>
          <div style={{ fontSize: 13, color: '#64748b' }}>{edu.degree}</div>
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {fmtDate(edu.startDate)} - {fmtDate(edu.endDate) || 'Present'}
        </div>
      </div>
      {edu.description && <div style={{ fontSize: 13, color: '#475569', marginTop: 4 }}>{edu.description}</div>}
    </div>
  );

  const renderProjectItem = (proj) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ fontWeight: 600, fontSize: 14, color: theme.primaryColor || '#2c3e50' }}>{proj.title}</div>
        <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {fmtDate(proj.startDate)} - {fmtDate(proj.endDate) || 'Present'}
        </div>
      </div>
      {proj.description && <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginTop: 6 }}>{proj.description}</div>}
      {proj.technologies && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
          {proj.technologies.split(',').map((t, j) => (
            <span key={j} style={{ fontSize: 11, color: '#64748b', background: '#f1f5f9', padding: '2px 8px', borderRadius: 4 }}>{t.trim()}</span>
          ))}
        </div>
      )}
      {proj.link && <a href={proj.link} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: theme.accentColor || '#3498db', marginTop: 4, display: 'inline-block' }}>View Project →</a>}
    </div>
  );

  const renderCertItem = (cert) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 13, color: theme.primaryColor || '#2c3e50' }}>{cert.name}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{cert.issuer}</div>
        </div>
        {cert.date && <div style={{ fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{fmtDate(cert.date)}</div>}
      </div>
      {cert.credentialURL && <a href={cert.credentialURL} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: theme.accentColor || '#3498db', marginTop: 2, display: 'inline-block' }}>Verify →</a>}
    </div>
  );

  const renderAwardItem = (award) => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 600, fontSize: 13, color: theme.primaryColor || '#2c3e50' }}>{award.title}</div>
      <div style={{ fontSize: 12, color: '#64748b' }}>{award.issuer}{award.date ? ` • ${fmtDate(award.date)}` : ''}</div>
      {award.description && <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{award.description}</div>}
    </div>
  );

  const renderSection = (key) => {
    switch (key) {
      case 'personalInfo': {
        const p = data.personalInfo;
        const contactItems = renderContact(p);
        return (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <h2 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: theme.primaryColor || '#2c3e50' }}>{p.fullName || 'Your Name'}</h2>
            {p.title && <div style={{ fontSize: 14, color: theme.accentColor || '#3498db', marginTop: 4, fontWeight: 500 }}>{p.title}</div>}
            {contactItems.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8, fontSize: 13, color: '#64748b' }}>
                {contactItems.map((item, i) => <span key={i}>{item}</span>)}
                {p.linkedin && <a href={p.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: theme.accentColor || '#3498db', textDecoration: 'none' }}>LinkedIn</a>}
                {p.github && <a href={p.github} target="_blank" rel="noopener noreferrer" style={{ color: theme.accentColor || '#3498db', textDecoration: 'none' }}>GitHub</a>}
              </div>
            )}
          </div>
        );
      }
      case 'summary':
        return data.summary ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Professional Summary" />
            <div style={{ fontSize: 13, color: '#475569', lineHeight: theme.lineHeight || 1.6 }}>{data.summary}</div>
          </div>
        ) : null;
      case 'education':
        return data.education.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Education" />
            {data.education.map((edu, i) => <div key={i}>{renderEducationItem(edu)}</div>)}
          </div>
        ) : null;
      case 'skills':
        return data.skills.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Skills" />
            {renderSkills()}
          </div>
        ) : null;
      case 'professionalExperience':
        return data.professionalExperience.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Experience" />
            {data.professionalExperience.map((exp, i) => <div key={i}>{renderExperienceItem(exp)}</div>)}
          </div>
        ) : null;
      case 'projects':
        return data.projects.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Projects" />
            {data.projects.map((proj, i) => <div key={i}>{renderProjectItem(proj)}</div>)}
          </div>
        ) : null;
      case 'certifications':
        return data.certifications.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Certifications" />
            {data.certifications.map((cert, i) => <div key={i}>{renderCertItem(cert)}</div>)}
          </div>
        ) : null;
      case 'awards':
        return data.awards.length > 0 ? (
          <div style={{ marginBottom: theme.sectionSpacing || 24 }}>
            <SectionTitle title="Awards" />
            {data.awards.map((award, i) => <div key={i}>{renderAwardItem(award)}</div>)}
          </div>
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div style={{ padding: 40, fontFamily: theme.font || 'Arial', fontSize: theme.fontSize || '14px', lineHeight: theme.lineHeight || 1.6, color: '#334155', maxWidth: 800, margin: '0 auto' }}>
      {isTwoColumn ? (
        <div style={{ display: 'flex', gap: 32 }}>
          <div style={{ flex: '0 0 280px' }}>
            {visibleSections.slice(0, Math.ceil(visibleSections.length / 2)).map(s => <div key={s}>{renderSection(s)}</div>)}
          </div>
          <div style={{ flex: 1 }}>
            {visibleSections.slice(Math.ceil(visibleSections.length / 2)).map(s => <div key={s}>{renderSection(s)}</div>)}
          </div>
        </div>
      ) : (
        visibleSections.map(s => <div key={s}>{renderSection(s)}</div>)
      )}
    </div>
  );
};

export default ProTemplate;
